// The household fulfillment lifecycle, mirroring the backend household_status
// enum. Ordered from earliest to latest stage.

export const HOUSEHOLD_STATUS_ORDER = [
  "new",
  "configuring",
  "ready_to_ship",
  "shipped",
  "ready_for_handoff",
  // The exception at the end: not a stage the flow advances through, but a
  // flag a house enters from any stage. A skip sets it; only an admin clears it.
  "support_needed",
] as const;

export type HouseholdStatus = (typeof HOUSEHOLD_STATUS_ORDER)[number];

/** snake_case status → human label shown in pills, dropdowns, and filters. */
export const HOUSEHOLD_STATUS_LABELS: Record<string, string> = {
  new: "New",
  configuring: "Configuring",
  ready_to_ship: "Ready to ship",
  shipped: "Shipped",
  ready_for_handoff: "Ready for handoff",
  support_needed: "Needs support",
};

/** Display label for a status, falling back to the raw value if unknown. */
export function householdStatusLabel(status: string): string {
  return HOUSEHOLD_STATUS_LABELS[status] ?? status;
}

/**
 * Which colour a pill takes. Kept apart from the enum so a derived state can
 * have a colour without being a status.
 */
export type StatusTone =
  "neutral" | "sky" | "warning" | "shipped" | "progress" | "success" | "support";

export interface DisplayStatus {
  label: string;
  tone: StatusTone;
}

function toneFor(status: string): StatusTone {
  switch (status) {
    case "configuring":
      return "sky";
    case "ready_to_ship":
      return "warning";
    case "shipped":
      return "shipped";
    case "ready_for_handoff":
      return "success";
    case "support_needed":
      return "support";
    default:
      return "neutral";
  }
}

/**
 * What the pill says, given the enum and the resident's progress.
 *
 * The enum has one value, `shipped`, for the whole stretch between the kit
 * leaving and the handoff. A house that has never opened the app and a house
 * on its sixth step both read "Shipped", and the list endpoint already knows
 * the difference. So for `shipped` the label comes from the counts: none done
 * is Not started, some is In progress, all is Done. A house with no applicable
 * steps has nothing left to do and reads Done, not Not started.
 *
 * Every other status keeps its own label. This is a display state, not a new
 * lifecycle value: nothing else in the system changes meaning.
 */
export function displayStatus(
  status: string,
  completedSteps: number,
  totalSteps: number,
): DisplayStatus {
  if (status === "support_needed") {
    // The flag outranks the counts: a flagged house that finished every step
    // still needs a person.
    return { label: "Needs support", tone: "support" };
  }
  if (status === "shipped") {
    if (completedSteps === 0 && totalSteps > 0) {
      return { label: "Not started", tone: "shipped" };
    }
    if (completedSteps >= totalSteps) {
      return { label: "Done", tone: "success" };
    }
    return { label: "In progress", tone: "progress" };
  }
  return { label: householdStatusLabel(status), tone: toneFor(status) };
}

/** Pill classes per tone. In progress takes the brand's daylight yellow: live, not finished. */
export function toneClass(tone: StatusTone): string {
  switch (tone) {
    case "sky":
      return "bg-sky/10 text-sky";
    case "warning":
      return "bg-warning/10 text-warning";
    case "shipped":
      return "bg-accent/10 text-accent-pressed";
    case "progress":
      return "bg-daylight/25 text-daylight-pressed";
    case "success":
      return "bg-success/10 text-success";
    case "support":
      return "bg-destructive/10 text-destructive";
    default:
      return "bg-surface-subtle text-muted";
  }
}
